'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  location: string;
  experience: string;
  expertise: string[];
  currentRevenue: string;
  linkedIn: string;
  website: string;
  motivation: string;
  availability: string;
}

interface FormErrors {
  [key: string]: string;
}

const ApplicationFormSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    location: '',
    experience: '',
    expertise: [],
    currentRevenue: '',
    linkedIn: '',
    website: '',
    motivation: '',
    availability: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const expertiseOptions = [
    'AI Strategy',
    'Change Management',
    'Digital Transformation',
    'Organizational Design',
    'Leadership Development',
    'Process Optimization',
    'Data Analytics',
    'Technology Implementation'
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    }

    if (step === 2) {
      if (!formData.company.trim()) newErrors.company = 'Company name is required';
      if (!formData.title.trim()) newErrors.title = 'Job title is required';
      if (!formData.location.trim()) newErrors.location = 'Location is required';
      if (!formData.experience) newErrors.experience = 'Experience level is required';
    }

    if (step === 3) {
      if (formData.expertise.length === 0) newErrors.expertise = 'Select at least one area of expertise';
      if (!formData.currentRevenue) newErrors.currentRevenue = 'Revenue range is required';
    }

    if (step === 4) {
      if (!formData.motivation.trim()) newErrors.motivation = 'Motivation is required';
      if (!formData.availability) newErrors.availability = 'Availability is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleExpertiseToggle = (expertise: string) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(expertise)
        ? prev.expertise.filter(e => e !== expertise)
        : [...prev.expertise, expertise]
    }));
    if (errors.expertise) {
      setErrors(prev => ({ ...prev, expertise: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setIsSubmitting(true);
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (!isHydrated) {
    return (
      <section id="apply" className="py-20 lg:py-32 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-background rounded-xl shadow-card p-8 border border-border">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="space-y-3 mt-8">
                  <div className="h-12 bg-muted rounded"></div>
                  <div className="h-12 bg-muted rounded"></div>
                  <div className="h-12 bg-muted rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (isSubmitted) {
    return (
      <section id="apply" className="py-20 lg:py-32 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-background rounded-xl shadow-card p-12 border border-border text-center">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
              </div>
              <h3 className="text-3xl font-bold font-headline text-primary mb-4">
                Application Submitted Successfully!
              </h3>
              <p className="text-lg text-muted-foreground font-body mb-8">
                Thank you for your interest in joining the AI Change Leaders network. Our partnership team will review your application and contact you within 5 business days.
              </p>
              <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                <h4 className="text-lg font-bold font-headline text-primary mb-3">
                  What Happens Next?
                </h4>
                <ul className="space-y-3 text-left">
                  <li className="flex items-start gap-3">
                    <Icon name="CheckIcon" size={20} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-foreground font-body">
                      Application review by our partnership team (3-5 business days)
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="CheckIcon" size={20} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-foreground font-body">
                      Discovery interview invitation if your profile matches our criteria
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="CheckIcon" size={20} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-foreground font-body">
                      Methodology training and network onboarding for approved partners
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="apply" className="py-20 lg:py-32 bg-card">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold font-headline text-primary mb-6">
              Apply to Join Our Network
            </h2>
            <p className="text-lg text-muted-foreground font-body">
              Complete the application to begin your journey as an AI Change Leaders partner.
            </p>
          </div>
          
          <div className="bg-background rounded-xl shadow-card p-8 border border-border">
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                {[1, 2, 3, 4].map((step) => (
                  <div key={step} className="flex items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold font-headline transition-all duration-300 ${
                        currentStep >= step
                          ? 'bg-secondary text-secondary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {step}
                    </div>
                    {step < 4 && (
                      <div
                        className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                          currentStep > step ? 'bg-secondary' : 'bg-muted'
                        }`}
                      ></div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-sm font-body text-muted-foreground">
                <span>Personal Info</span>
                <span>Professional</span>
                <span>Expertise</span>
                <span>Motivation</span>
              </div>
            </div>

            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold font-headline text-primary mb-4">
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-foreground font-body mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                        errors.firstName ? 'border-error' : 'border-border'
                      }`}
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="text-error text-sm mt-1 font-body">{errors.firstName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-foreground font-body mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                        errors.lastName ? 'border-error' : 'border-border'
                      }`}
                      placeholder="Smith"
                    />
                    {errors.lastName && (
                      <p className="text-error text-sm mt-1 font-body">{errors.lastName}</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                      errors.email ? 'border-error' : 'border-border'
                    }`}
                    placeholder="john.smith@example.com"
                  />
                  {errors.email && (
                    <p className="text-error text-sm mt-1 font-body">{errors.email}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                      errors.phone ? 'border-error' : 'border-border'
                    }`}
                    placeholder="+44 20 1234 5678"
                  />
                  {errors.phone && (
                    <p className="text-error text-sm mt-1 font-body">{errors.phone}</p>
                  )}
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold font-headline text-primary mb-4">
                  Professional Background
                </h3>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                      errors.company ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Your Company Ltd"
                  />
                  {errors.company && (
                    <p className="text-error text-sm mt-1 font-body">{errors.company}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                      errors.title ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Senior Consultant"
                  />
                  {errors.title && (
                    <p className="text-error text-sm mt-1 font-body">{errors.title}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                      errors.location ? 'border-error' : 'border-border'
                    }`}
                    placeholder="London, UK"
                  />
                  {errors.location && (
                    <p className="text-error text-sm mt-1 font-body">{errors.location}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Years of Experience *
                  </label>
                  <select
                    value={formData.experience}
                    onChange={(e) => handleInputChange('experience', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                      errors.experience ? 'border-error' : 'border-border'
                    }`}
                  >
                    <option value="">Select experience level</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10-15">10-15 years</option>
                    <option value="15-20">15-20 years</option>
                    <option value="20+">20+ years</option>
                  </select>
                  {errors.experience && (
                    <p className="text-error text-sm mt-1 font-body">{errors.experience}</p>
                  )}
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold font-headline text-primary mb-4">
                  Expertise & Practice
                </h3>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-3">
                    Areas of Expertise * (Select all that apply)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {expertiseOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handleExpertiseToggle(option)}
                        className={`px-4 py-3 rounded-lg border text-left font-body transition-all duration-300 ${
                          formData.expertise.includes(option)
                            ? 'bg-secondary text-secondary-foreground border-secondary'
                            : 'bg-background text-foreground border-border hover:border-secondary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              formData.expertise.includes(option)
                                ? 'bg-secondary-foreground border-secondary-foreground'
                                : 'border-border'
                            }`}
                          >
                            {formData.expertise.includes(option) && (
                              <Icon name="CheckIcon" size={16} variant="solid" className="text-secondary" />
                            )}
                          </div>
                          <span>{option}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  {errors.expertise && (
                    <p className="text-error text-sm mt-2 font-body">{errors.expertise}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Current Annual Revenue Range *
                  </label>
                  <select
                    value={formData.currentRevenue}
                    onChange={(e) => handleInputChange('currentRevenue', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                      errors.currentRevenue ? 'border-error' : 'border-border'
                    }`}
                  >
                    <option value="">Select revenue range</option>
                    <option value="0-100k">£0 - £100,000</option>
                    <option value="100k-250k">£100,000 - £250,000</option>
                    <option value="250k-500k">£250,000 - £500,000</option>
                    <option value="500k-1m">£500,000 - £1,000,000</option>
                    <option value="1m+">£1,000,000+</option>
                  </select>
                  {errors.currentRevenue && (
                    <p className="text-error text-sm mt-1 font-body">{errors.currentRevenue}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    LinkedIn Profile
                  </label>
                  <input
                    type="url"
                    value={formData.linkedIn}
                    onChange={(e) => handleInputChange('linkedIn', e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body"
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Company Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body"
                    placeholder="https://yourcompany.com"
                  />
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold font-headline text-primary mb-4">
                  Motivation & Availability
                </h3>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Why do you want to join AI Change Leaders? *
                  </label>
                  <textarea
                    value={formData.motivation}
                    onChange={(e) => handleInputChange('motivation', e.target.value)}
                    rows={6}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body resize-none ${
                      errors.motivation ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Tell us about your interest in joining our network, your goals, and how you see yourself contributing to AI transformation projects..."
                  />
                  {errors.motivation && (
                    <p className="text-error text-sm mt-1 font-body">{errors.motivation}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-foreground font-body mb-2">
                    Availability for New Projects *
                  </label>
                  <select
                    value={formData.availability}
                    onChange={(e) => handleInputChange('availability', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body ${
                      errors.availability ? 'border-error' : 'border-border'
                    }`}
                  >
                    <option value="">Select availability</option>
                    <option value="immediate">Immediate (within 2 weeks)</option>
                    <option value="1-month">Within 1 month</option>
                    <option value="2-3-months">2-3 months</option>
                    <option value="flexible">Flexible / Project-dependent</option>
                  </select>
                  {errors.availability && (
                    <p className="text-error text-sm mt-1 font-body">{errors.availability}</p>
                  )}
                </div>
                
                <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                  <div className="flex items-start gap-3">
                    <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-sm font-bold font-headline text-primary mb-2">
                        Application Review Process
                      </h4>
                      <p className="text-sm text-foreground font-body">
                        Our partnership team reviews all applications within 5 business days. If your profile matches our criteria, we'll invite you to a discovery interview to discuss collaboration opportunities and network fit.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className={`px-6 py-3 rounded-lg font-semibold font-cta transition-all duration-300 flex items-center gap-2 ${
                  currentStep === 1
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : 'bg-background text-foreground border border-border hover:bg-muted'
                }`}
              >
                <Icon name="ChevronLeftIcon" size={20} variant="outline" />
                Previous
              </button>
              
              {currentStep < 4 ? (
                <button
                  onClick={handleNext}
                  className="px-6 py-3 bg-secondary text-secondary-foreground rounded-lg font-semibold font-cta hover:bg-secondary/90 transition-all duration-300 flex items-center gap-2"
                >
                  Next
                  <Icon name="ChevronRightIcon" size={20} variant="outline" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`px-8 py-3 rounded-lg font-semibold font-cta transition-all duration-300 flex items-center gap-2 ${
                    isSubmitting
                      ? 'bg-muted text-muted-foreground cursor-not-allowed'
                      : 'bg-accent text-accent-foreground hover:bg-accent/90 shadow-subtle hover:shadow-strong'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationFormSection;